import { useCallback, useMemo, useRef } from 'react';

import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import useSideDetail from '#/hooks/useSideDetail';
import ListAddButton from '#/modules/commonComponents/table/ListAddButton';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import { DEFAULT_LIST_SORT_FIELD_LABELS } from '#/modules/commonComponents/table/listFilterKeys';
import { useCan } from '#/modules/infra/auth/useCan';
import PageShell from '#/modules/infra/layout/PageShell';
import SideMsgLayout from '#/modules/infra/layout/SideMsgLayout';
import StockAdjustmentSideMsg from '#/modules/imsMgmt/receipts/stockAdjustment/components/StockAdjustmentSideMsg';
import { buildStockAdjustmentColumns } from '#/modules/imsMgmt/receipts/stockAdjustment/data/stockAdjustmentColumns';
import { getStockAdjustmentApiErrorMessage } from '#/modules/imsMgmt/receipts/stockAdjustment/helper/apiError';
import { applyAdjustmentDateRangeToFilters } from '#/modules/imsMgmt/receipts/stockAdjustment/helper/adjustmentDateListFilter';
import useStockAdjustmentList from '#/modules/imsMgmt/receipts/stockAdjustment/hooks/useStockAdjustmentList';

const STOCK_ADJUSTMENT_SORT_FIELD_LABELS = {
  ...DEFAULT_LIST_SORT_FIELD_LABELS,
  adjustmentNo: '調整單號',
  adjustmentDate: '單據日',
  status: '狀態',
};

const StockAdjustmentListPage = () => {
  const listRef = useRef(null);
  const { displayData, setSelectedData } = useSideDetail();
  const canCreate = useCan(PERM.STOCK_ADJUSTMENT_CREATE);
  const canUpdate = useCan(PERM.STOCK_ADJUSTMENT_UPDATE);

  const {
    rows,
    pagination,
    isLoading,
    isError,
    error,
    refetch,
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    setSort,
    removeSortAt,
    resetListQuery,
    setListQuery,
  } = useStockAdjustmentList();

  const setAdjustmentDateRangeFilter = useCallback((range) => {
    setListQuery((prev) => ({
      ...prev,
      page: 1,
      filters: applyAdjustmentDateRangeToFilters(prev.filters, range),
    }));
  }, [setListQuery]);

  const handleFilterChange = useCallback((key, value) => {
    if (key === 'adjustmentDateFrom' && value === undefined) {
      setAdjustmentDateRangeFilter(undefined);
      return;
    }
    setFilter(key, value);
  }, [setFilter, setAdjustmentDateRangeFilter]);

  const columns = useMemo(
    () => buildStockAdjustmentColumns({
      filters: listQuery.filters || {},
      onFilterChange: handleFilterChange,
      onAdjustmentDateRangeChange: setAdjustmentDateRangeFilter,
      canShowEdit: (row) => canUpdate && row.status === 'DRAFT',
    }),
    [listQuery.filters, handleFilterChange, setAdjustmentDateRangeFilter, canUpdate],
  );

  const filterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(columns, { filters: listQuery.filters }),
    [columns, listQuery.filters],
  );

  const handleSortingChange = useCallback((sorting) => {
    setSort((sorting ?? []).map((item) => ({
      id: item.id,
      desc: Boolean(item.desc),
    })));
  }, [setSort]);

  const handleRowClick = useCallback((row, isSelected) => {
    if (isSelected) {
      setSelectedData(row.original);
      return;
    }

    setSelectedData(null);
  }, [setSelectedData]);

  const headerAction = canCreate ? (
    <ListAddButton to={ROUTES.stockAdjustmentCreate} />
  ) : null;

  return (
    <PageShell variant="listWithSide">
      <div className="layout-shell__body flex min-h-0 flex-col">
        <h1 className="page_title shrink-0">
          <span className="border-l-5 border-primary-400 pl-2.5">調整管理</span>
        </h1>
        <SideMsgLayout>
          <div className="table-panel min-h-0 xl:h-full">
            <ListTablePanel
              ref={listRef}
              tableKey="ims.stock_adjustment.list"
              columns={columns}
              rows={rows}
              pagination={pagination}
              isLoading={isLoading}
              isError={isError}
              errorMessage={isError ? getStockAdjustmentApiErrorMessage(error, '載入列表失敗') : undefined}
              onRetry={isError ? () => { refetch(); } : undefined}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              onRowClick={handleRowClick}
              onSearch={setKeyword}
              onReset={resetListQuery}
              initialKeyword={listQuery.keyword}
              searchPlaceholder="搜尋單號、開單原因、備註、庫存編號或物料"
              headerAction={headerAction}
              listQuery={listQuery}
              filterStatusFields={filterStatusFields}
              sortFieldLabels={STOCK_ADJUSTMENT_SORT_FIELD_LABELS}
              onFilterChange={handleFilterChange}
              onKeywordClear={() => setKeyword('')}
              onSortRemove={removeSortAt}
              onSortingChange={handleSortingChange}
            />
          </div>

          <StockAdjustmentSideMsg
            displayData={displayData}
            setSelectedData={setSelectedData}
          />
        </SideMsgLayout>
      </div>
    </PageShell>
  );
};

export default StockAdjustmentListPage;
